import { useMemo, useState } from "react";

import ChooseEntityDialog from "@/components/ChooseEntityDialog";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useUser } from "@/context/user-context";
import { ToastType } from "@/enums";
import { Entity, EntityType, useAddTraceToTable, useListSheets } from "@/queries";
import { displayToast } from "@/utils/toast";

interface AddTraceToDatasetDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  traceId: string | null;
}

const AddTraceToDatasetDialog = ({
  isOpen,
  onOpenChange,
  traceId,
}: AddTraceToDatasetDialogProps) => {
  const [selectedTable, setSelectedTable] = useState<Entity<EntityType.SMART_TABLE> | undefined>();
  const workspaceId = useUser()?.activeWorkspaceId;
  const { mutateAsync: addTraceToTable, isLoading } = useAddTraceToTable();

  const selectedTableId = selectedTable?.id !== undefined ? String(selectedTable.id) : undefined;
  const sheetsQuery = useListSheets(selectedTableId, { limit: 100 });

  const targetSheet = useMemo(() => {
    const sheets = sheetsQuery.data?.items;
    if (!sheets?.length) {
      return undefined;
    }

    return [...sheets].sort((a, b) => a.index - b.index)[0];
  }, [sheetsQuery.data?.items]);

  const canSubmit = !!traceId && !!workspaceId && !!selectedTableId && !!targetSheet && !isLoading;

  const handleConfirm = async () => {
    if (!traceId || !workspaceId || !selectedTableId || !targetSheet) {
      return;
    }

    try {
      const response = await addTraceToTable({
        smart_table_id: selectedTableId,
        sheet_id: targetSheet.id,
        trace_id: traceId,
      });

      if (response.success) {
        displayToast(
          "Trace added to dataset",
          ToastType.success,
          `/workspace/${workspaceId}/smart-tables/${selectedTableId}?sheet=${targetSheet.id}`,
        );
        onOpenChange(false);
        return;
      }

      displayToast(
        response.error ?? response.message ?? "Could not add trace to dataset",
        ToastType.error,
      );
    } catch {
      displayToast("Could not add trace to dataset", ToastType.error);
    }
  };

  return (
    <Dialog onOpenChange={onOpenChange} open={isOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Trace to Dataset</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-2">
          <p className="text-sm text-gray-600">Select a dataset to add this trace as a new row.</p>
          <ChooseEntityDialog
            type={EntityType.SMART_TABLE}
            onSelect={setSelectedTable as (table: Entity) => void}
            selectedEntity={selectedTable as Entity | undefined}
            currentFolderId={selectedTable?.folder_id ?? null}
            readonly={isLoading}
          />
          {selectedTableId && sheetsQuery.isLoading && (
            <p className="text-xs text-gray-500">Loading sheets...</p>
          )}
          {selectedTableId && !sheetsQuery.isLoading && !targetSheet && (
            <p className="text-xs text-red-600">This dataset has no sheets available.</p>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} isLoading={isLoading} disabled={!canSubmit}>
            Add trace
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddTraceToDatasetDialog;
